import React, { useState, useEffect } from 'react';
import {
  DENTAL_RISK_DOMAINS,
  DENTAL_RISK_CATEGORIES,
  INCIDENT_CRITERIA_PRESETS,
  WAREHOUSES_LIST
} from '../lib/constants';
import DentalIncidentQuickSelector from './DentalIncidentQuickSelector';
import { DentalSeverityBadge, SeveritySelector, getSeverityInfo } from './DentalSeverityBadge';
import DentalRiskUploadZone from './DentalRiskUploadZone';
import { SeverityGuideModal } from './risk/SeverityGuideModal';

/**
 * Format datetime-local string (YYYY-MM-DDTHH:mm)
 */
const getLocalDateTimeString = (dateObj = new Date()) => {
  const d = new Date(dateObj);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const DentalRiskWizardModal = ({
  isOpen = false,
  onClose = () => {},
  onSaveReport = () => {},
  currentUser = null,
  userRole = '',
  warehouses = WAREHOUSES_LIST
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [showSeverityGuide, setShowSeverityGuide] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    incident_date: getLocalDateTimeString(),
    location: 'ไม่ระบุ',
    risk_domain: 'clinical',
    category: '',
    sub_category: '',
    title: '',
    is_patient_involved: false,
    patient_hn: '',
    procedure_name: '',
    instrument_barcode: '',
    sterile_cycle_id: '',
    machine_id: '',
    severity_level: 'C',
    severity_score: 3,
    description: '',
    immediate_action: '',
    contributing_factors: '',
    image_urls: [],
    is_anonymous: false,
    reporter_name: '',
    reporter_role: '',
    contact_info: ''
  });

  // กำหนดค่าเริ่มต้นเมื่อเปิด Modal
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setValidationError('');
      const defaultCategories = DENTAL_RISK_CATEGORIES.clinical;
      setFormData({
        incident_date: getLocalDateTimeString(),
        location: 'ไม่ระบุ',
        risk_domain: 'clinical',
        category: defaultCategories[0]?.category || '',
        sub_category: defaultCategories[0]?.subCategories[0] || '',
        title: '',
        is_patient_involved: false,
        patient_hn: '',
        procedure_name: '',
        instrument_barcode: '',
        sterile_cycle_id: '',
        machine_id: '',
        severity_level: 'C',
        severity_score: 3,
        description: '',
        immediate_action: '',
        contributing_factors: '',
        image_urls: [],
        is_anonymous: false,
        reporter_name: currentUser?.name || currentUser?.email?.split('@')[0] || '',
        reporter_role: userRole || 'บุคลากรทันตกรรม',
        contact_info: ''
      });
    }
  }, [isOpen, currentUser, userRole]);

  if (!isOpen) return null;

  // รายการหมวดหมู่ตาม Domain ปัจจุบัน
  const availableCategories = DENTAL_RISK_CATEGORIES[formData.risk_domain] || [];
  const currentCategoryObj = availableCategories.find((c) => c.category === formData.category) || availableCategories[0];
  const availableSubCategories = currentCategoryObj?.subCategories || [];

  // เมื่อสลับ Domain
  const handleDomainChange = (domain) => {
    const defaultCats = DENTAL_RISK_CATEGORIES[domain] || [];
    const defaultLevel = (domain === 'clinical' || domain === 'cssd') ? 'C' : '2';
    const severityInfo = getSeverityInfo(defaultLevel, domain);

    setFormData((prev) => ({
      ...prev,
      risk_domain: domain,
      category: defaultCats[0]?.category || '',
      sub_category: defaultCats[0]?.subCategories[0] || '',
      severity_level: defaultLevel,
      severity_score: severityInfo?.score || 1
    }));
  };

  // Fast Incident Selector Click
  const handleSelectIncident = (incident) => {
    if (!incident) return;
    const tag = incident.tag || incident.title;
    const preset = incident.domain ? incident : (INCIDENT_CRITERIA_PRESETS[tag] || {});
    const severityScore = preset.severity_score || getSeverityInfo(preset.severity_level, preset.domain)?.score || 1;

    setFormData((prev) => ({
      ...prev,
      title: tag,
      risk_domain: preset.domain || prev.risk_domain,
      severity_level: preset.severity_level || prev.severity_level,
      severity_score: severityScore,
      category: preset.category || prev.category,
      sub_category: preset.sub_category || prev.sub_category,
      location: preset.location || prev.location,
      is_patient_involved: typeof preset.is_patient_involved === 'boolean' ? preset.is_patient_involved : prev.is_patient_involved
    }));
  };

  // Severity Change
  const handleSeverityChange = (code) => {
    const info = getSeverityInfo(code, formData.risk_domain);
    setFormData((prev) => ({
      ...prev,
      severity_level: code,
      severity_score: info?.score || 1
    }));
  };

  // Step Validation & Navigation
  const handleNextStep = () => {
    setValidationError('');

    if (currentStep === 1) {
      if (!formData.title.trim()) {
        setValidationError('กรุณาระบุหัวข้ออุบัติการณ์');
        return;
      }
      if (!formData.location) {
        setValidationError('กรุณาเลือกสถานที่เกิดเหตุ');
        return;
      }
      if (formData.is_patient_involved && !formData.patient_hn.trim() && !formData.procedure_name.trim()) {
        setValidationError('กรณีเกี่ยวข้องกับผู้ป่วย กรุณาระบุ HN หรือชื่อหัตถการ');
        return;
      }
    } else if (currentStep === 2) {
      if (!formData.severity_level) {
        setValidationError('กรุณาเลือกระดับความรุนแรง');
        return;
      }
    } else if (currentStep === 3) {
      if (!formData.description.trim()) {
        setValidationError('กรุณากรอกรายละเอียดเหตุการณ์ที่เกิดขึ้น');
        return;
      }
    }

    setCurrentStep((prev) => Math.min(prev + 1, 4));
  };

  const handlePrevStep = () => {
    setValidationError('');
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setValidationError('');

    if (!formData.description.trim()) {
      setValidationError('กรุณากรอกรายละเอียดเหตุการณ์');
      setCurrentStep(3);
      return;
    }

    setIsSubmitting(true);
    try {
      const incidentDateIso = formData.incident_date
        ? new Date(formData.incident_date).toISOString()
        : new Date().toISOString();

      const payload = {
        ...formData,
        incident_date: incidentDateIso,
        reporter_name: formData.is_anonymous ? null : (formData.reporter_name || 'ไม่ประสงค์ระบุ'),
        reporter_role: formData.is_anonymous ? null : formData.reporter_role,
        contact_info: formData.is_anonymous ? null : formData.contact_info,
        status: 'pending'
      };

      await onSaveReport(payload);
      onClose();
    } catch (err) {
      console.error('Submit report error:', err);
      setValidationError('เกิดข้อผิดพลาดในการบันทึกรายงาน: ' + (err.message || ''));
    } finally {
      setIsSubmitting(false);
    }
  };

  // รายการสถานที่ที่จัดกลุ่มสวยงาม โดยมี 'ไม่ระบุ' เป็นตัวเลือกแรก
  const rawCleanLocations = warehouses.map((w) => (typeof w === 'string' ? w : w.name.trim().replace(/^-\s*/, '')));
  const combinedLocations = Array.from(new Set([
    'ไม่ระบุ',
    'ห้องจ่ายกลาง (CSSD)',
    'จุดล้างและทำลายเชื้อ',
    ...rawCleanLocations,
    ...(formData.location ? [formData.location] : [])
  ]));
  const cleanLocations = ['ไม่ระบุ', ...combinedLocations.filter((l) => l !== 'ไม่ระบุ')];


  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px'
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '780px',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}
      >
        {/* Header Bar */}
        <div
          style={{
            padding: '18px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f8fafc'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                backgroundColor: '#fee2e2',
                color: '#ef4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '22px'
              }}
            >
              🛡️
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.25em', fontWeight: '800', color: '#0f172a' }}>
                รายงานความเสี่ยงทันตกรรม (Dental Risk Incident)
              </h3>
              <div style={{ fontSize: '0.82em', color: '#64748b' }}>
                ขั้นตอนที่ {currentStep} จาก 4: {
                  currentStep === 1 ? 'บริบทเหตุการณ์' :
                  currentStep === 2 ? 'ประเมินระดับความรุนแรง' :
                  currentStep === 3 ? 'รายละเอียด & หลักฐาน' : 'ข้อมูลผู้รายงาน & ยืนยัน'
                }
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              color: '#94a3b8',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '8px'
            }}
          >
            ✕
          </button>
        </div>

        {/* Step Indicator Wizard Bar */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #f1f5f9'
          }}
        >
          {[
            { num: 1, title: 'บริบทเหตุการณ์', icon: '📍' },
            { num: 2, title: 'ระดับความรุนแรง', icon: '⚖️' },
            { num: 3, title: 'รายละเอียด & รูป', icon: '📝' },
            { num: 4, title: 'ผู้รายงาน & ยืนยัน', icon: '🔒' }
          ].map((step) => {
            const isActive = currentStep === step.num;
            const isDone = currentStep > step.num;
            return (
              <div
                key={step.num}
                onClick={() => {
                  if (step.num < currentStep) setCurrentStep(step.num);
                }}
                style={{
                  padding: '12px 6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  borderBottom: isActive ? '3px solid #0284c7' : isDone ? '3px solid #10b981' : '3px solid transparent',
                  backgroundColor: isActive ? '#f0f9ff' : 'transparent',
                  cursor: step.num < currentStep ? 'pointer' : 'default',
                  transition: 'all 0.2s ease'
                }}
              >
                <span
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: isActive ? '#0284c7' : isDone ? '#10b981' : '#e2e8f0',
                    color: isActive || isDone ? '#ffffff' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75em',
                    fontWeight: 'bold'
                  }}
                >
                  {isDone ? '✓' : step.num}
                </span>
                <span
                  style={{
                    fontSize: '0.82em',
                    fontWeight: isActive ? '700' : '500',
                    color: isActive ? '#0284c7' : isDone ? '#10b981' : '#64748b'
                  }}
                >
                  {step.title}
                </span>
              </div>
            );
          })}
        </div>

        {/* Modal Scrollable Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {validationError && (
            <div
              style={{
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                color: '#b91c1c',
                padding: '10px 14px',
                borderRadius: '10px',
                fontSize: '0.86em',
                fontWeight: '600',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>⚠️</span>
              <span>{validationError}</span>
            </div>
          )}

          {/* ==================== STEP 1: บริบทเหตุการณ์ ==================== */}
          {currentStep === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Comprehensive Incident Selector */}
              <div>
                <DentalIncidentQuickSelector
                  selectedTitle={formData.title}
                  onSelectIncident={handleSelectIncident}
                />
              </div>

              {/* Domain Selector */}
              <div>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.9em', color: '#1e293b', marginBottom: '6px' }}>
                  ประเภทความเสี่ยง (Risk Domain) *
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '10px' }}>
                  {DENTAL_RISK_DOMAINS.map((domain) => {
                    const isSelected = formData.risk_domain === domain.id;
                    return (
                      <button
                        key={domain.id}
                        type="button"
                        onClick={() => handleDomainChange(domain.id)}
                        style={{
                          padding: '12px 14px',
                          borderRadius: '12px',
                          border: isSelected ? `2.5px solid ${domain.color}` : '1.5px solid #cbd5e1',
                          backgroundColor: isSelected ? `${domain.color}10` : '#ffffff',
                          color: isSelected ? domain.color : '#475569',
                          fontWeight: isSelected ? '700' : '600',
                          fontSize: '0.9em',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          justifyContent: 'center',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <span style={{ fontSize: '1.2em' }}>{domain.icon}</span>
                        <span>{domain.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>


              {/* หัวข้ออุบัติการณ์ */}
              <div>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.9em', color: '#1e293b', marginBottom: '6px' }}>
                  หัวข้ออุบัติการณ์ (Incident Title) *
                </label>
                <input
                  type="text"
                  placeholder="เช่น หัวกรอ Diamond Bur หักขณะกรอฟัน, เข็มฉีดยาชาทิ่มนิ้วมือ"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.95em',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* วันเวลา และ สถานที่ */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ fontWeight: '700', fontSize: '0.88em', color: '#1e293b' }}>
                      วัน-เวลาที่เกิดเหตุ *
                    </label>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, incident_date: getLocalDateTimeString(new Date()) })}
                      style={{
                        background: '#f0f9ff',
                        border: '1px solid #bae6fd',
                        borderRadius: '6px',
                        padding: '2px 8px',
                        fontSize: '0.76em',
                        color: '#0284c7',
                        fontWeight: '700',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      title="แตะเพื่อลงเวลาปัจจุบันทันที"
                    >
                      <span>🕒 ลงเวลาปัจจุบัน</span>
                    </button>
                  </div>
                  <input
                    type="datetime-local"
                    value={formData.incident_date}
                    onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.9em',
                      boxSizing: 'border-box'
                    }}
                  />
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.74em', color: '#64748b', lineHeight: '1.3' }}>
                    หากไม่ทราบเวลาแน่ชัด ให้กดปุ่ม <b>"🕒 ลงเวลาปัจจุบัน"</b> เพื่อบันทึกเวลาปัจจุบัน
                  </p>
                </div>
                <div>
                  <label htmlFor="modal-incident-location" style={{ display: 'block', fontWeight: '700', fontSize: '0.88em', color: '#1e293b', marginBottom: '6px' }}>
                    สถานที่เกิดเหตุ *
                  </label>
                  <select
                    id="modal-incident-location"
                    aria-label="สถานที่เกิดเหตุ"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.9em',
                      backgroundColor: '#ffffff',
                      boxSizing: 'border-box'
                    }}
                  >
                    {cleanLocations.map((loc, idx) => (
                      <option key={idx} value={loc}>
                        {loc}
                      </option>
                    ))}
                  </select>
                </div>

              </div>

              {/* หมวดหมู่และหมวดหมู่ย่อย */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div>
                  <label style={{ display: 'block', fontWeight: '700', fontSize: '0.88em', color: '#1e293b', marginBottom: '6px' }}>
                    หมวดหมู่ความเสี่ยง
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => {
                      const newCat = e.target.value;
                      const catObj = availableCategories.find((c) => c.category === newCat);
                      setFormData({
                        ...formData,
                        category: newCat,
                        sub_category: catObj?.subCategories[0] || ''
                      });
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.88em',
                      backgroundColor: '#ffffff',
                      boxSizing: 'border-box'
                    }}
                  >
                    {availableCategories.map((c, idx) => (
                      <option key={idx} value={c.category}>
                        {c.category}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: '700', fontSize: '0.88em', color: '#1e293b', marginBottom: '6px' }}>
                    หมวดหมู่ย่อย
                  </label>
                  <select
                    value={formData.sub_category}
                    onChange={(e) => setFormData({ ...formData, sub_category: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: '1.5px solid #cbd5e1',
                      fontSize: '0.88em',
                      backgroundColor: '#ffffff',
                      boxSizing: 'border-box'
                    }}
                  >
                    {availableSubCategories.map((sc, idx) => (
                      <option key={idx} value={sc}>
                        {sc}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* ผู้ป่วยเกี่ยวข้องหรือไม่ */}
              <div
                style={{
                  padding: '14px',
                  borderRadius: '12px',
                  backgroundColor: '#f8fafc',
                  border: '1.5px solid #e2e8f0'
                }}
              >
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    fontWeight: '700',
                    color: '#0f172a',
                    fontSize: '0.92em'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.is_patient_involved}
                    onChange={(e) => setFormData({ ...formData, is_patient_involved: e.target.checked })}
                    style={{ width: '18px', height: '18px', accentColor: '#0284c7' }}
                  />
                  <span>เหตุการณ์นี้มีผู้ป่วยได้รับผลกระทบโดยตรง (Patient Involved)</span>
                </label>

                {formData.is_patient_involved && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82em', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>
                        เลขประจำตัวผู้ป่วย (HN)
                      </label>
                      <input
                        type="text"
                        placeholder="เช่น 69-001234"
                        value={formData.patient_hn}
                        onChange={(e) => setFormData({ ...formData, patient_hn: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.88em',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.82em', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>
                        ชื่อหัตถการ / การรักษา
                      </label>
                      <input
                        type="text"
                        placeholder="เช่น อุดฟัน Composite, ผ่าฟันคุด"
                        value={formData.procedure_name}
                        onChange={(e) => setFormData({ ...formData, procedure_name: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #cbd5e1',
                          fontSize: '0.88em',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* ข้อมูลเครื่องมือและรอบนึ่ง (CSSD & Instrument details) */}
              {(formData.risk_domain === 'cssd' || formData.risk_domain === 'clinical') && (
                <div
                  style={{
                    padding: '14px',
                    borderRadius: '12px',
                    backgroundColor: '#f0fdfa',
                    border: '1.5px solid #99f6e4'
                  }}
                >
                  <div style={{ fontWeight: '700', fontSize: '0.88em', color: '#0f766e', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>🧼</span>
                    <span>ข้อมูลเครื่องมือ / รอบนึ่งฆ่าเชื้อ (CSSD Traceability)</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8em', fontWeight: '600', color: '#047857', marginBottom: '4px' }}>
                        บาร์โค้ดเครื่องมือ/เซ็ต (Barcode)
                      </label>
                      <input
                        type="text"
                        placeholder="เช่น INS-SET-042"
                        value={formData.instrument_barcode || ''}
                        onChange={(e) => setFormData({ ...formData, instrument_barcode: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #99f6e4',
                          fontSize: '0.86em',
                          boxSizing: 'border-box',
                          backgroundColor: '#ffffff'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8em', fontWeight: '600', color: '#047857', marginBottom: '4px' }}>
                        รหัสรอบนึ่งฆ่าเชื้อ (Sterile Cycle ID)
                      </label>
                      <input
                        type="text"
                        placeholder="เช่น CYCLE-202609-01"
                        value={formData.sterile_cycle_id || ''}
                        onChange={(e) => setFormData({ ...formData, sterile_cycle_id: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #99f6e4',
                          fontSize: '0.86em',
                          boxSizing: 'border-box',
                          backgroundColor: '#ffffff'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.8em', fontWeight: '600', color: '#047857', marginBottom: '4px' }}>
                        เครื่องฆ่าเชื้อ / Autoclave
                      </label>
                      <input
                        type="text"
                        placeholder="เช่น AUTOCLAVE-01"
                        value={formData.machine_id || ''}
                        onChange={(e) => setFormData({ ...formData, machine_id: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #99f6e4',
                          fontSize: '0.86em',
                          boxSizing: 'border-box',
                          backgroundColor: '#ffffff'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ==================== STEP 2: ระดับความรุนแรง ==================== */}
          {currentStep === 2 && (
            <div>
              <div
                style={{
                  marginBottom: '14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}
              >
                <div>
                  <h4 style={{ margin: '0 0 6px 0', fontSize: '1.05em', color: '#0f172a', fontWeight: '800' }}>
                    ประเมินระดับความรุนแรง ({(formData.risk_domain === 'clinical' || formData.risk_domain === 'cssd') ? 'Level A - I' : 'Non-Clinical Level 1 - 5'})
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.84em', color: '#64748b' }}>
                    เกณฑ์มาตรฐานระดับประเทศ (National Risk / Incident Reporting Guideline) สำหรับงานทันตกรรม
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSeverityGuide(true)}
                  style={{
                    background: '#eff6ff',
                    border: '1.5px solid #bfdbfe',
                    color: '#1d4ed8',
                    borderRadius: '10px',
                    padding: '6px 12px',
                    fontSize: '0.82em',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 1px 3px rgba(37, 99, 235, 0.1)',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#dbeafe';
                    e.currentTarget.style.borderColor = '#93c5fd';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#eff6ff';
                    e.currentTarget.style.borderColor = '#bfdbfe';
                  }}
                >
                  <span>ℹ️</span>
                  <span>คู่มือระดับความรุนแรง & เกณฑ์เวลาทำ RCA</span>
                </button>
              </div>

              <SeveritySelector
                domain={formData.risk_domain}
                value={formData.severity_level}
                onChange={handleSeverityChange}
              />
            </div>
          )}

          {/* ==================== STEP 3: รายละเอียด & การแก้ไขเฉพาะหน้า & รูป ==================== */}
          {currentStep === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.9em', color: '#1e293b', marginBottom: '6px' }}>
                  รายละเอียดเหตุการณ์ที่เกิดขึ้น (Event Description) *
                </label>
                <textarea
                  rows="4"
                  placeholder="อธิบายลำดับเหตุการณ์ว่าเกิดอะไรขึ้น ที่ไหน อย่างไร ผู้ป่วยหรือบุคลากรได้รับผลกระทบอย่างไร..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.92em',
                    boxSizing: 'border-box',
                    lineHeight: '1.5'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.9em', color: '#1e293b', marginBottom: '6px' }}>
                  การแก้ไขปัญหาเบื้องต้น / การดูแลเฉพาะหน้า (Immediate Action Taken)
                </label>
                <textarea
                  rows="2"
                  placeholder="ระบุสิ่งที่ได้ดำเนินการแก้ไขทันทีเพื่อความปลอดภัย เช่น ปฐมพยาบาล, หยุดหัตถการ, ส่งต่อ ER, อายัดเครื่องมือ..."
                  value={formData.immediate_action}
                  onChange={(e) => setFormData({ ...formData, immediate_action: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.92em',
                    boxSizing: 'border-box',
                    lineHeight: '1.5'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.9em', color: '#1e293b', marginBottom: '6px' }}>
                  ปัจจัยที่ส่งผลให้เกิดเหตุการณ์ (Contributing Factors)
                </label>
                <input
                  type="text"
                  placeholder="เช่น แสงสว่างไม่เพียงพอ, เครื่องมือเสื่อมสภาพ, ความเร่งรีบ, การสื่อสารคลาดเคลื่อน"
                  value={formData.contributing_factors}
                  onChange={(e) => setFormData({ ...formData, contributing_factors: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1.5px solid #cbd5e1',
                    fontSize: '0.9em',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: '700', fontSize: '0.9em', color: '#1e293b', marginBottom: '6px' }}>
                  แนบภาพถ่ายหลักฐาน (Photos / Evidence)
                </label>
                <DentalRiskUploadZone
                  images={formData.image_urls}
                  onChange={(urls) => setFormData({ ...formData, image_urls: urls })}
                  maxImages={5}
                />
              </div>
            </div>
          )}

          {/* ==================== STEP 4: ข้อมูลผู้รายงาน & สรุปยืนยัน ==================== */}
          {currentStep === 4 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Anonymous Toggle */}
              <div
                style={{
                  padding: '16px',
                  borderRadius: '14px',
                  backgroundColor: formData.is_anonymous ? '#fef2f2' : '#f0fdf4',
                  border: formData.is_anonymous ? '1.5px solid #fca5a5' : '1.5px solid #bbf7d0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <div>
                  <div style={{ fontWeight: '800', color: formData.is_anonymous ? '#991b1b' : '#166534', fontSize: '0.96em' }}>
                    {formData.is_anonymous ? '🔒 รายงานแบบไม่ประสงค์ออกนาม (Anonymous)' : '✍️ ระบุชื่อผู้รายงาน'}
                  </div>
                  <div style={{ fontSize: '0.82em', color: formData.is_anonymous ? '#b91c1c' : '#15803d', marginTop: '2px' }}>
                    {formData.is_anonymous
                      ? 'ระบบจะไม่จัดเก็บชื่อและข้อมูลติดต่อของคุณ เหมาะสำหรับรายงานปัญหาเพื่อปรับปรุงระบบ'
                      : 'สามารถระบุชื่อเพื่อความสะดวกในการประสานงานติดตามผล'}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_anonymous: !formData.is_anonymous })}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '10px',
                    backgroundColor: formData.is_anonymous ? '#ef4444' : '#10b981',
                    color: '#ffffff',
                    border: 'none',
                    fontWeight: '700',
                    fontSize: '0.84em',
                    cursor: 'pointer'
                  }}
                >
                  {formData.is_anonymous ? 'เปลี่ยนเป็นระบุชื่อ' : 'เปลี่ยนเป็นนิรนาม'}
                </button>
              </div>

              {!formData.is_anonymous && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85em', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
                      ชื่อ-สกุล ผู้รายงาน
                    </label>
                    <input
                      type="text"
                      value={formData.reporter_name}
                      onChange={(e) => setFormData({ ...formData, reporter_name: e.target.value })}
                      placeholder="ระบุชื่อผู้รายงาน"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.9em',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.85em', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
                      ตำแหน่ง / บทบาท
                    </label>
                    <input
                      type="text"
                      value={formData.reporter_role}
                      onChange={(e) => setFormData({ ...formData, reporter_role: e.target.value })}
                      placeholder="เช่น ทันตแพทย์, ผู้ช่วยฯ, เจ้าหน้าที่ CSSD"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.9em',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.85em', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
                      เบอร์โทรติดต่อกลับ
                    </label>
                    <input
                      type="text"
                      value={formData.contact_info}
                      onChange={(e) => setFormData({ ...formData, contact_info: e.target.value })}
                      placeholder="เช่น 081-xxx-xxxx"
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #cbd5e1',
                        fontSize: '0.9em',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Summary Preview Card */}
              <div
                style={{
                  padding: '16px',
                  borderRadius: '14px',
                  backgroundColor: '#f8fafc',
                  border: '1.5px solid #e2e8f0'
                }}
              >
                <div style={{ fontWeight: '800', color: '#0f172a', fontSize: '0.95em', marginBottom: '10px' }}>
                  📋 ตรวจสอบสรุปข้อมูลรายงาน
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.86em', marginBottom: '10px' }}>
                  <div>
                    <span style={{ color: '#64748b' }}>หัวข้อ:</span> <strong>{formData.title || '-'}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>สถานที่:</span> <strong>{formData.location}</strong>
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>ความรุนแรง:</span>{' '}
                    <DentalSeverityBadge level={formData.severity_level} domain={formData.risk_domain} size="sm" />
                  </div>
                  <div>
                    <span style={{ color: '#64748b' }}>หมวดหมู่:</span> {formData.category}
                  </div>
                  {formData.is_patient_involved && (
                    <div>
                      <span style={{ color: '#64748b' }}>ผู้ป่วย:</span> HN {formData.patient_hn || '-'} ({formData.procedure_name || '-'})
                    </div>
                  )}
                  {formData.instrument_barcode && (
                    <div>
                      <span style={{ color: '#64748b' }}>บาร์โค้ดเครื่องมือ:</span> <strong>{formData.instrument_barcode}</strong>
                    </div>
                  )}
                  {formData.sterile_cycle_id && (
                    <div>
                      <span style={{ color: '#64748b' }}>รอบนึ่ง:</span> <strong>{formData.sterile_cycle_id}</strong>
                    </div>
                  )}
                  <div>
                    <span style={{ color: '#64748b' }}>รูปภาพแนบ:</span> {formData.image_urls.length} รูป
                  </div>
                </div>

                <div style={{ fontSize: '0.84em', color: '#334155', backgroundColor: '#ffffff', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', maxHeight: '80px', overflowY: 'auto' }}>
                  <strong>รายละเอียด:</strong> {formData.description}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Action Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div>
            {currentStep > 1 && (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handlePrevStep}
                style={{
                  padding: '10px 18px',
                  borderRadius: '10px',
                  backgroundColor: '#ffffff',
                  color: '#475569',
                  border: '1.5px solid #cbd5e1',
                  fontWeight: '600',
                  fontSize: '0.9em',
                  cursor: 'pointer'
                }}
              >
                ← ย้อนกลับ
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={onClose}
              style={{
                padding: '10px 18px',
                borderRadius: '10px',
                backgroundColor: '#ffffff',
                color: '#64748b',
                border: '1.5px solid #cbd5e1',
                fontWeight: '600',
                fontSize: '0.9em',
                cursor: 'pointer'
              }}
            >
              ยกเลิก
            </button>

            {currentStep < 4 ? (
              <button
                type="button"
                onClick={handleNextStep}
                style={{
                  padding: '10px 22px',
                  borderRadius: '10px',
                  backgroundColor: '#0284c7',
                  color: '#ffffff',
                  border: 'none',
                  fontWeight: '700',
                  fontSize: '0.9em',
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(2, 132, 199, 0.3)'
                }}
              >
                ถัดไป →
              </button>
            ) : (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleSubmit}
                style={{
                  padding: '10px 24px',
                  borderRadius: '10px',
                  backgroundColor: '#059669',
                  color: '#ffffff',
                  border: 'none',
                  fontWeight: '700',
                  fontSize: '0.9em',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 2px 6px rgba(5, 150, 105, 0.3)'
                }}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner" style={{ display: 'inline-block', width: '14px', height: '14px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    กำลังบันทึก...
                  </>
                ) : (
                  <>
                    <span>🚀</span>
                    <span>ยืนยันและส่งรายงาน</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Severity Guide Modal Popup */}
      <SeverityGuideModal
        isOpen={showSeverityGuide}
        onClose={() => setShowSeverityGuide(false)}
        initialTab={(formData.risk_domain === 'clinical' || formData.risk_domain === 'cssd') ? 'clinical' : 'non_clinical'}
        selectedLevel={formData.severity_level}
        onSelectLevel={handleSeverityChange}
      />
    </div>
  );
};

export default DentalRiskWizardModal;
